import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { Skeleton } from "@runcheetah/ui";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@runcheetah/ui";
import { InfoTooltip, Notice } from "@/components/common";
import { ChevronLeft, PlusIcon, ListIcon, ChevronsUpDown } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { canRetryFailedGeneration } from "@/components/campaign-review/utils";

import { buildSocialPostPlannerRequest } from "./build-planner-request";
import {
  EMPTY_BRANDKIT,
  buildDefaultPlannerPrompt,
  type PlannerBrandPresence,
  type PlannerGenerationMode,
  type PlannerPostType,
} from "./constants";
import { BrandkitPanel } from "./components/brandkit-panel";
import { PlannerOutput, type PlannerView } from "./components/planner-output";
import { PlannerRunForm } from "./components/planner-run-form";
import { PlannerTaskList } from "./components/planner-task-list";
import { TaskStatusBadge } from "./components/task-status-badge";
import { getCarouselReviewState } from "./components/carousel-slide-review";
import type { SceneBlock } from "./types";
import { formatDate, formatDuration, getSceneBlocksFlat } from "./utils";

export function SocialPostPlannerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPinnedBrandRoute = Boolean(id);
  const brandIdFromQuery = searchParams.get("brandId");
  const utils = trpc.useUtils();

  const { data: brands, isPending: brandsPending } = trpc.admin.brands.list.useQuery({});

  const defaultBrandId = useMemo(() => {
    if (!brands || brands.length === 0) {
      return null;
    }

    return brands.find((brand) => brand.status === "scanned")?.id ?? brands[0]!.id;
  }, [brands]);

  const brandId = id ?? brandIdFromQuery ?? defaultBrandId ?? "";

  const [brandPresence, setBrandPresence] = useState<PlannerBrandPresence>("branded");
  const [prompt, setPrompt] = useState(() =>
    buildDefaultPlannerPrompt({ brandPresence: "branded" }),
  );
  const [hasCustomPrompt, setHasCustomPrompt] = useState(false);
  const [postType, setPostType] = useState<PlannerPostType>("feed_portrait");
  const [generationMode, setGenerationMode] = useState<PlannerGenerationMode>("auto");
  const [layoutTemplateId, setLayoutTemplateId] = useState("");
  const [carouselFormatKey, setCarouselFormatKey] = useState("");
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedCarouselSlideId, setSelectedCarouselSlideId] = useState<string | null>(null);
  const [selectedSceneBlockId, setSelectedSceneBlockId] = useState<string | null>(null);
  const [view, setView] = useState<PlannerView>("creative");

  useEffect(() => {
    if (isPinnedBrandRoute || brandIdFromQuery || !defaultBrandId) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.set("brandId", defaultBrandId);
    setSearchParams(next, { replace: true });
  }, [defaultBrandId, brandIdFromQuery, isPinnedBrandRoute, searchParams, setSearchParams]);

  useEffect(() => {
    setSelectedTaskId(null);
    setSelectedSceneBlockId(null);
  }, [brandId]);

  const {
    data: brand,
    isPending: brandPending,
    error: brandError,
  } = trpc.admin.brands.get.useQuery({ id: brandId }, { enabled: !!brandId });

  const tasksQuery = trpc.admin.brands.socialPostGenerationTasks.useQuery(
    { brandId },
    {
      enabled: !!brandId,
      refetchInterval: (query) => {
        const tasks = query.state.data;
        return tasks?.some((task) => task.status === "queued" || task.status === "processing")
          ? 2000
          : false;
      },
    },
  );

  const publishedLayoutTemplatesQuery = trpc.admin.socialPostLayoutTemplates.list.useQuery({
    publishedOnly: true,
  });
  const carouselFormatsQuery = trpc.admin.carouselFormats.list.useQuery({ activeOnly: true });

  const enqueueMutation = trpc.admin.brands.enqueueSocialPostGeneration.useMutation({
    onSuccess: async (data) => {
      setSelectedTaskId(data.generationTaskId);
      await utils.admin.brands.socialPostGenerationTasks.invalidate({ brandId });
      await utils.admin.brands.socialPostGenerationTask.invalidate({
        brandId,
        generationTaskId: data.generationTaskId,
      });
    },
  });

  const tasks = tasksQuery.data;

  const selectedTaskDetailsQuery = trpc.admin.brands.socialPostGenerationTask.useQuery(
    selectedTaskId
      ? {
          brandId,
          generationTaskId: selectedTaskId,
        }
      : {
          brandId,
          generationTaskId: "00000000-0000-0000-0000-000000000000",
        },
    {
      enabled: !!brandId && !!selectedTaskId,
      refetchInterval: (query) => {
        const task = query.state.data;
        return task && (task.status === "queued" || task.status === "processing") ? 2000 : false;
      },
    },
  );

  const selectedTask = useMemo(
    () => tasks?.find((task) => task.id === selectedTaskId) ?? tasks?.[0] ?? null,
    [selectedTaskId, tasks],
  );

  const selectedTaskDetails = selectedTaskDetailsQuery.data ?? null;
  const selectedCarousel = selectedTaskDetails?.carousel ?? null;
  const selectedCarouselState = getCarouselReviewState(selectedCarousel, selectedCarouselSlideId);
  const selectedPlan =
    selectedCarouselState.selectedPlan ??
    (selectedCarousel
      ? null
      : (selectedTaskDetails?.result?.plan ?? selectedTask?.result?.plan ?? null));
  const selectedPlanBlocksFlat = useMemo(
    () => (selectedPlan ? getSceneBlocksFlat(selectedPlan) : []),
    [selectedPlan],
  );
  const selectedSceneBlock: SceneBlock | null =
    selectedPlanBlocksFlat.find((block) => block.id === selectedSceneBlockId) ?? null;

  const layoutTemplateOptions = useMemo(
    () =>
      (publishedLayoutTemplatesQuery.data ?? []).flatMap((template) => {
        const publishedTemplate = template.publishedTemplate;
        if (!publishedTemplate?.supportedPostTypes.includes(postType)) {
          return [];
        }

        return [
          {
            templateId: template.templateId,
            name: publishedTemplate.name,
            description: publishedTemplate.description,
          },
        ];
      }),
    [postType, publishedLayoutTemplatesQuery.data],
  );
  const carouselFormatOptions = useMemo(
    () =>
      (carouselFormatsQuery.data ?? []).map((format) => ({
        key: format.key,
        name: format.name,
        minSlides: format.minSlides,
        maxSlides: format.maxSlides,
      })),
    [carouselFormatsQuery.data],
  );

  useEffect(() => {
    if (!selectedTaskId && tasks?.[0]) {
      setSelectedTaskId(tasks[0].id);
    }
  }, [selectedTaskId, tasks]);

  useEffect(() => {
    setSelectedCarouselSlideId(null);
  }, [selectedTask?.id]);

  useEffect(() => {
    if (
      layoutTemplateId &&
      !layoutTemplateOptions.some((template) => template.templateId === layoutTemplateId)
    ) {
      setLayoutTemplateId("");
    }
  }, [layoutTemplateId, layoutTemplateOptions]);

  useEffect(() => {
    if (
      carouselFormatKey &&
      !carouselFormatOptions.some((format) => format.key === carouselFormatKey)
    ) {
      setCarouselFormatKey("");
    }
  }, [carouselFormatKey, carouselFormatOptions]);

  useEffect(() => {
    if (hasCustomPrompt) {
      return;
    }

    setPrompt(buildDefaultPlannerPrompt({ brandPresence, sourceDomain: brand?.sourceDomain }));
  }, [brand?.sourceDomain, brandPresence, hasCustomPrompt]);

  useEffect(() => {
    if (!selectedPlan) {
      setSelectedSceneBlockId(null);
      return;
    }

    if (
      selectedSceneBlockId &&
      selectedPlanBlocksFlat.some((block) => block.id === selectedSceneBlockId)
    ) {
      return;
    }

    setSelectedSceneBlockId(selectedPlanBlocksFlat[0]?.id ?? null);
  }, [selectedPlan, selectedPlanBlocksFlat, selectedSceneBlockId]);

  const handleSubmit = () => {
    enqueueMutation.mutate({
      brandId,
      request: buildSocialPostPlannerRequest({
        prompt,
        postType,
        brandPresence,
        generationMode,
        layoutTemplateId,
        carouselFormatKey,
      }),
    });
  };

  const handlePromptChange = (value: string) => {
    setHasCustomPrompt(true);
    setPrompt(value);
  };

  if (!brandId) {
    if (brandsPending) {
      return (
        <div className="space-y-4">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      );
    }

    return (
      <div className="flex h-64 flex-col items-center justify-center gap-3 rounded-lg border border-dashed">
        <p className="text-lg font-medium">No brands available</p>
        <p className="text-sm text-muted-foreground">
          Add and scan a brand first to start using Social Planner.
        </p>
      </div>
    );
  }

  if (brandPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (brandError || !brand) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-3">
        <p className="text-lg font-medium">Failed to load brand</p>
        <p className="text-sm text-muted-foreground">{brandError?.message}</p>
      </div>
    );
  }

  const plannerDisabled = brand.status !== "scanned" || enqueueMutation.isPending;

  const handleBrandChange = (nextBrandId: string) => {
    if (isPinnedBrandRoute) {
      navigate(`/social-post-planner?brandId=${nextBrandId}`);
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.set("brandId", nextBrandId);
    setSearchParams(next, { replace: false });
  };

  const summary = selectedTaskDetails?.result?.summary ?? null;
  const backHref = isPinnedBrandRoute ? `/brands/${brandId}` : "/brands";
  const selectedTaskStatus = selectedTaskDetails?.status ?? selectedTask?.status;
  const canRetrySelectedTask = canRetryFailedGeneration({
    brandStatus: brand.status,
    taskStatus: selectedTaskStatus,
    hasActiveRun: false,
    hasRequest: Boolean(selectedTask?.request) && !selectedTask?.scheduledPostId,
  });
  const handleRetrySelectedTask = () => {
    if (!selectedTask || !canRetrySelectedTask) {
      return;
    }

    enqueueMutation.mutate({
      brandId,
      request: selectedTask.request,
    });
  };

  return (
    <div className="-m-6 flex h-[calc(100vh-3.5rem)] flex-col bg-muted/20">
      {/* Top header bar */}
      <div className="flex shrink-0 flex-col gap-3 border-b bg-background px-4 py-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 gap-2 text-muted-foreground"
            asChild
          >
            <Link to={backHref}>
              <ChevronLeft className="size-4" />
              Back
            </Link>
          </Button>
          <div className="w-56 shrink-0 xl:w-72">
            <select
              id="planner-brand"
              value={brandId}
              onChange={(event) => handleBrandChange(event.target.value)}
              className="h-8 w-full rounded-md border bg-background px-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
            >
              {(brands ?? []).map((brandOption) => (
                <option key={brandOption.id} value={brandOption.id}>
                  {brandOption.sourceDomain}
                </option>
              ))}
              {isPinnedBrandRoute && !brands?.some((brandOption) => brandOption.id === brand.id) ? (
                <option value={brand.id}>{brand.sourceDomain}</option>
              ) : null}
            </select>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              Social Planner
            </div>
            <div className="truncate text-sm text-muted-foreground">
              Custom one-off social generation outside the main campaign flow.
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2 rounded-xl">
                <ListIcon className="size-4" />
                Recent Attempts
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="flex w-[420px] flex-col gap-0 sm:w-[560px] sm:max-w-none"
            >
              <SheetHeader className="border-b p-5">
                <SheetTitle>Recent Generation Attempts</SheetTitle>
                <SheetDescription>
                  {tasks?.length ?? 0} attempts for {brand.sourceDomain}. Select one to inspect its
                  creative output and technical trace.
                </SheetDescription>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto p-5">
                {tasksQuery.isPending ? (
                  <div className="space-y-3">
                    <Skeleton className="h-24 w-full" />
                    <Skeleton className="h-24 w-full" />
                  </div>
                ) : tasksQuery.error ? (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {tasksQuery.error.message}
                  </div>
                ) : (
                  <PlannerTaskList
                    tasks={tasks ?? []}
                    selectedTaskId={selectedTask?.id ?? null}
                    onSelect={setSelectedTaskId}
                  />
                )}
              </div>
            </SheetContent>
          </Sheet>

          <Sheet>
            <SheetTrigger asChild>
              <Button size="sm" className="gap-2 rounded-xl">
                <PlusIcon className="size-4" />
                New Generation
              </Button>
            </SheetTrigger>
            <SheetContent className="w-[400px] overflow-y-auto sm:w-[540px] sm:max-w-none">
              <SheetHeader>
                <SheetTitle>New Generation Attempt</SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                <PlannerRunForm
                  prompt={prompt}
                  postType={postType}
                  brandPresence={brandPresence}
                  generationMode={generationMode}
                  layoutTemplateId={layoutTemplateId}
                  carouselFormatKey={carouselFormatKey}
                  layoutTemplateOptions={layoutTemplateOptions}
                  carouselFormatOptions={carouselFormatOptions}
                  layoutTemplatesLoading={publishedLayoutTemplatesQuery.isPending}
                  carouselFormatsLoading={carouselFormatsQuery.isPending}
                  layoutTemplatesError={publishedLayoutTemplatesQuery.error?.message ?? null}
                  carouselFormatsError={carouselFormatsQuery.error?.message ?? null}
                  onPromptChange={handlePromptChange}
                  onPostTypeChange={setPostType}
                  onBrandPresenceChange={setBrandPresence}
                  onGenerationModeChange={setGenerationMode}
                  onLayoutTemplateIdChange={setLayoutTemplateId}
                  onCarouselFormatKeyChange={setCarouselFormatKey}
                  disabled={plannerDisabled}
                  isPending={enqueueMutation.isPending}
                  error={enqueueMutation.error?.message ?? null}
                  brandStatus={brand.status}
                  onSubmit={handleSubmit}
                />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Sub-header: task meta + summary + view switcher */}
      {selectedTask ? (
        <div className="shrink-0 border-b bg-background">
          <div className="flex flex-col gap-3 px-4 py-3 xl:flex-row xl:items-center xl:justify-between">
            {/* Left: status, date, duration, cost, summary */}
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
              <div className="flex shrink-0 items-center gap-2 rounded-full border bg-muted/20 px-2 py-1">
                <TaskStatusBadge status={selectedTaskDetails?.status ?? selectedTask.status} />
                <span className="text-xs text-muted-foreground">
                  {formatDate(selectedTaskDetails?.updatedAt ?? selectedTask.updatedAt)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatDuration(
                    selectedTask.createdAt,
                    selectedTaskDetails?.updatedAt ?? selectedTask.updatedAt,
                  )}
                </span>
                <span className="font-mono text-xs text-muted-foreground">
                  $
                  {selectedTaskDetails?.llmCosts
                    ? `${selectedTaskDetails.llmCosts.totalCostUsd.toFixed(3)}${
                        selectedTaskDetails.llmCosts.unpricedEventCount > 0
                          ? ` +${selectedTaskDetails.llmCosts.unpricedEventCount} unpriced`
                          : ""
                      }`
                    : "--"}
                </span>
              </div>

              {summary ? (
                <>
                  <div className="hidden h-4 w-px shrink-0 bg-border sm:block" />
                  <p className="min-w-0 truncate text-xs text-muted-foreground" title={summary}>
                    {summary}
                  </p>
                </>
              ) : null}
            </div>

            {/* Right: view switcher */}
            <div className="flex shrink-0 items-center gap-2 xl:ml-4">
              <InfoTooltip
                label="Explain planner views"
                content="Creative shows the generated post and editable scene structure. Technical shows request payloads, cost traces, and raw generation data."
                side="left"
              />
              <Tabs value={view} onValueChange={(v) => setView(v as PlannerView)}>
                <TabsList className="h-8">
                  <TabsTrigger value="creative" className="px-3 text-xs">
                    Creative
                  </TabsTrigger>
                  <TabsTrigger value="technical" className="px-3 text-xs">
                    Technical
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>

          {/* Errors & warnings */}
          {(selectedTaskDetails?.lastError ?? selectedTask.lastError) ||
          selectedTaskDetails?.result?.warnings?.length ? (
            <div className="flex flex-col gap-2 border-t px-4 py-2.5">
              {(selectedTaskDetails?.lastError ?? selectedTask.lastError) ? (
                <Notice tone="danger" title="Error" className="px-4 py-2.5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <span>{selectedTaskDetails?.lastError ?? selectedTask.lastError}</span>
                    {selectedTask.status === "failed" && !selectedTask.scheduledPostId ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-fit rounded-xl bg-background"
                        disabled={!canRetrySelectedTask || enqueueMutation.isPending}
                        onClick={handleRetrySelectedTask}
                      >
                        {enqueueMutation.isPending ? "Retrying..." : "Retry"}
                      </Button>
                    ) : null}
                  </div>
                </Notice>
              ) : null}

              {selectedTaskDetails?.result?.warnings?.length ? (
                <Notice tone="warning" title="Warnings" className="px-4 py-2.5">
                  {selectedTaskDetails.result.warnings.join(" · ")}
                </Notice>
              ) : null}
            </div>
          ) : null}

          {/* Brandkit — collapsible, applies to entire creative */}
          {selectedTaskDetails?.brandkit ? (
            <Collapsible className="border-t">
              <CollapsibleTrigger className="flex w-full items-center justify-between px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:bg-muted/30 transition-colors">
                <span>Brandkit</span>
                <ChevronsUpDown className="size-3.5" />
              </CollapsibleTrigger>
              <CollapsibleContent className="border-t px-4 py-3">
                <BrandkitPanel brandkit={selectedTaskDetails.brandkit ?? EMPTY_BRANDKIT} />
              </CollapsibleContent>
            </Collapsible>
          ) : null}
        </div>
      ) : null}

      {/* Main content area */}
      <div className="flex min-h-0 flex-1">
        <PlannerOutput
          view={view}
          selectedTask={selectedTask}
          selectedTaskDetails={selectedTaskDetails}
          selectedTaskDetailsQuery={{
            isPending: selectedTaskDetailsQuery.isPending,
            error: selectedTaskDetailsQuery.error
              ? { message: selectedTaskDetailsQuery.error.message }
              : null,
          }}
          selectedPlan={selectedPlan}
          carousel={selectedCarousel}
          selectedCarouselSlideId={selectedCarouselState.selectedSlideId}
          selectedSceneBlock={selectedSceneBlock}
          selectedSceneBlockId={selectedSceneBlockId}
          onSelectCarouselSlideId={setSelectedCarouselSlideId}
          onSelectSceneBlock={setSelectedSceneBlockId}
        />
      </div>
    </div>
  );
}
